// File: Pages/Reports/Reports.jsx

import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAPI } from '../../Contexts/APIContext'
import { useAuth } from '../../Contexts/AuthContext'
import { REPORT_TYPES, REPORT_TYPE_TO_QUERY, QUERY_TO_REPORT_TYPE } from './utils/reportTypes'
import ReportTypeSelector from './components/ReportTypeSelector'
import DynamicFAQList from './components/FAQ/DynamicFAQList'
import DynamicForm from './components/forms/DynamicForm'
import {
  ReportConfigProvider,
  useReportConfig,
} from './utils/ReportConfigProvider'
import styles from './index.module.css'
import errorToast from '../../Components/Toasts/ErrorToast'
import successToast from '../../Components/Toasts/SuccessToast'

/**
 * Inner Reports component that uses the configuration
 */
const ReportsContent = () => {
  const { getCurrentUser } = useAuth()
  const { submitReport } = useAPI()
  const location = useLocation()
  const navigate = useNavigate()
  const {
    isLoading: isLoadingConfig,
    formConfigs,
    faqData,
    reportTypes,
    faqConfig,
    error: configError,
  } = useReportConfig()

  const [prevPageData, setPrevPageData] = useState({
    url: '',
    title: '',
  })

  const [reportType, setReportType] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Parse report type from URL query parameter on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const reportTypeParam = searchParams.get('reportType')

    if (reportTypeParam && QUERY_TO_REPORT_TYPE[reportTypeParam]) {
      setReportType(QUERY_TO_REPORT_TYPE[reportTypeParam])
    }
  }, [location.search])

  // Get previous page information from history state or referrer
  useEffect(() => {
    // Try state first (if someone navigated programmatically with state)
    if (location.state && location.state.from) {
      setPrevPageData({
        url: location.state.from,
        title: location.state.title || 'Recently Visited Item',
      })
    }
    // Then try document.referrer
    else if (!!document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer)
        if (referrerUrl.origin === window.location.origin) {
          setPrevPageData({
            url: document.referrer,
            title: 'Recently Visited Item',
          })
        }
      } catch (err) {
        console.warn(err)
      }
    }
    // Check localStorage as a fallback
    else {
      const prevUrl = localStorage.getItem('prevPageUrl')
      const prevTitle = localStorage.getItem('prevPageTitle')

      if (prevUrl) {
        setPrevPageData({
          url: prevUrl,
          title: prevTitle || 'Recently Visited Item',
        })
      }
    }
  }, [location])

  // Display configuration error
  useEffect(() => {
    if (configError) {
      errorToast(configError)
    }
  }, [configError])

  // Handle report type selection
  const handleReportTypeChange = type => {
    setReportType(type)

    // Update URL query parameter
    const queryParam = REPORT_TYPE_TO_QUERY[type]
    if (queryParam) {
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.set('reportType', queryParam)
      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true })
    }
  }


  const handleSubmitReport = async formData => {
    // console.log('Form Data:', formData)

    setIsSubmitting(true)

    const reportTypeKey = Object.keys(REPORT_TYPES).find(
      k => REPORT_TYPES[k] === reportType
    )

    if (!reportTypeKey) {
      console.error('Unknown report type:', reportType)
      setIsSubmitting(false)
      return
    }

    // Parse feelsItemLink to extract objectType and objectId
    let objectType = formData.objectType || ''
    let objectId = formData.objectId || ''

    if (formData.feelsItemLink && !objectType) {
      try {
        const url = new URL(formData.feelsItemLink)
        const pathParts = url.pathname.split('/').filter(Boolean)

        // URL pattern matching
        const patterns = [
          { pattern: ['create-new-feel-step-2'], type: 'song', idIndex: 1 },
          { pattern: ['song', 'feels'], type: 'song', idIndex: 2 },
          { pattern: ['song', '*', 'backdrop'], type: 'song', idIndex: 1 },
          { pattern: ['edit-song'], type: 'song', idIndex: 1 },
          { pattern: ['songs', 'artist'], type: 'artist', idIndex: 2 },
          { pattern: ['songs', 'album'], type: 'album', idIndex: 2 },
          { pattern: ['feels', 'artist'], type: 'artist', idIndex: 2 },
          { pattern: ['albums'], type: 'artist', idIndex: 1 },
          { pattern: ['feel'], type: 'feels', idIndex: 1 },
          { pattern: ['mm'], type: 'feels', idIndex: 1 },
        ]

        // Find matching pattern
        for (const { pattern, type, idIndex } of patterns) {
          if (pattern.every((p, i) => p === '*' || pathParts[i] === p)) {
            objectType = type
            objectId = pathParts[idIndex]
            break
          }
        }

        // Fallback to default parsing
        if (!objectType && pathParts.length >= 2) {
          objectType = pathParts[0]
          objectId = pathParts[1]
        }
      } catch (error) {
        console.error('Error parsing feelsItemLink:', error)
      }
    }

    try {
      const report = {
        report_type: reportTypeKey,
        report_text: formData.notes || '',
        object: objectType || '',
        object_f_id: objectId || '',
        report_origin: 'FAST',
        user_f_id: getCurrentUser()?.userId || '',
        report_user_id: getCurrentUser()?.email || '',
        department: 'Feels Factory', // Default department

        report_data: {
          previous_page: prevPageData.url,
        },
      }

      if (formData.feelsItemLink) {
        report.report_data.item_link = formData.feelsItemLink
      }

      if (formData.youtubeItemLink) {
        report.report_data.youtube_url = formData.youtubeItemLink
      }

      if (formData.artistId) {
        report.report_data.artist_f_id = formData.artistId
        report.object = 'artist'
        report.object_f_id = formData.artistId
      }

      if (formData.artistName) {
        report.report_data.artist_name = formData.artistName.toLowerCase()
      }

      if (formData.artistDisplayName || formData.artistName) {
        report.report_data.artist_display_name =
          formData.artistDisplayName || formData.artistName
      }

      switch (reportTypeKey) {
        case 'ADD_ARTIST_TO_ORG':
          report.department = 'MIT'

          report.report_data.new_organization_name =
            formData.organizationName || ''
          report.report_data.label_start_date = formData.labelStartDate || ''
          report.report_data.all_rights_carry_forward =
            formData.rightsCarryForward || 'no'
          report.report_data.prior_releases_carry_forward =
            formData.priorReleases || []
          report.report_data.previous_label_name =
            formData.previousLabelName || ''
          report.report_data.previous_label_start_date =
            formData.previousLabelStartDate || ''
          report.report_data.previous_label_end_date =
            formData.previousLabelEndDate || ''
          break

        case 'DISASSOCIATE_CONTENT':
          report.department = 'MIT'
          break

        case 'INITIAL_ACCESS_REQUEST':
          report.report_data.organization_name = formData.organizationName || ''
          report.report_data.website = formData.website || ''
          report.report_data.role = formData.role || ''
          break
      }

      const report_response = await submitReport(report)
      // console.log("report_response", report_response)

      successToast('Your report has been successfully submitted.')
      setSubmitted(true)
    } catch (error) {
      console.error('Error submitting report:', error)
      errorToast('Failed to submit report. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle submitting another report
  const handleSubmitAnother = () => {
    setReportType('')
    setSubmitted(false)

    // Clear URL query parameter
    navigate(location.pathname, { replace: true })
  }

  // Render the form based on the selected report type
  const renderForm = () => {
    if (!reportType) return null

    // Get the form config for the selected report type
    const formConfig = formConfigs[reportType]
    if (!formConfig) return null

    return (
      <div>
        <h2 className={styles.sectionTitle}>{reportType} Form</h2>
        <p className={styles.sectionSubtitle}>
          Please fill out the form below to submit your {reportType.toLowerCase()} report
        </p>
        <DynamicForm
          formConfig={formConfig}
          onSubmit={handleSubmitReport}
          isSubmitting={isSubmitting}
          prevPageUrl={prevPageData.url}
          prevPageTitle={prevPageData.title}
        />
      </div>
    )
  }

  // Render thank you message after submission
  const renderThankYou = () => {
    return (
      <div className={styles.thankyouContainer}>
        <h2 className={styles.thankyouTitle}>Thank You For Your Report</h2>
        <p className={styles.thankyouMessage}>
          Your report has been submitted successfully. Our team will review it
          and take appropriate action.
        </p>
        <button className={styles.button} onClick={handleSubmitAnother}>
          Submit Another Report
        </button>
      </div>
    )
  }

  // Show loading state if configuration is still loading
  if (isLoadingConfig) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Reports</h1>
            <p className={styles.welcomeMessage}>
              Report content issues or request access
            </p>
          </div>
        </div>
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Reports</h1>
          <p className={styles.welcomeMessage}>
            Report content issues or request access
          </p>
        </div>
      </div>

      {isSubmitting && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner}></div>
        </div>
      )}

      {submitted ? (
        renderThankYou()
      ) : (
        <div className={styles.formContainer}>
          {/* Report Type Selector */}
          <ReportTypeSelector
            selectedType={reportType}
            onTypeChange={handleReportTypeChange}
            reportTypeOptions={reportTypes}
          />

          {/* FAQ List - shown when report type is selected */}
          {reportType && (
            <DynamicFAQList
              reportType={reportType}
              faqData={faqData}
              faqConfig={faqConfig}
            />
          )}

          {/* Form - shown with FAQ when report type is selected */}
          {renderForm()}
        </div>
      )}
    </div>
  )
}

/**
 * Main Reports component that integrates the configuration provider
 */
const Reports = () => {
  return (
    <ReportConfigProvider>
      <ReportsContent />
    </ReportConfigProvider>
  )
}

export default Reports
